import { DesktopLayout } from '@/components/layout/desktopLayout'
import { queryClient } from '@/config/query/queryClient'
import { getWagmiConfig } from '@/config/wagmi'
import '@fontsource-variable/geist'
import { HydrationBoundary, QueryClientProvider, dehydrate } from '@tanstack/react-query'
import globalStyles from '@vaultsfyi/common/styles/global.css?url'
import { type ReactNode, useState } from 'react'
import {
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from 'react-router'
import { PreventFlashOnWrongTheme, ThemeProvider, useTheme } from 'remix-themes'
import { WagmiProvider, cookieToInitialState } from 'wagmi'
import { TooltipProvider } from './components/common/tooltip'
import { ConnectWalletModalProvider } from './components/connect/connectModal'
import { ErrorPage } from './components/shared/errorPage'
import {
  APY_RANGE_SETTING_COOKIE_KEY,
  CHARTS_PERIOD_SETTINGS_COOKIE_KEY,
  COLLAPSE_DASHBOARD_RETURNS_COOKIE_KEY,
  EXPAND_NAVIGATION_COOKIE_KEY,
  INCLUDE_REWARDS_SETTING_COOKIE_KEY,
  TVL_RANGE_SETTING_COOKIE_KEY,
} from './constants/cookie'
import { APP_META_DESCRIPTION, APP_META_TITLE } from './constants/meta'
import { AccountProvider } from './context/account'
import { LayoutProvider } from './context/layout'
import { MixpanelProvider } from './context/mixpanel'
import {
  DEFAULT_SETTINGS,
  type Settings,
  SettingsProvider,
  VALID_APY_RANGES,
  VALID_CHART_PERIODS,
} from './context/settings'
import { SoundProvider } from './context/sound'
import { useViewTransition } from './hooks/useViewTransition'
import { polyfills } from './polyfills.client'
import { themeSessionResolver } from './sessions.server'
import appStyles from './tailwind.css?url'
import type { VaultApyRange } from './types/apy'
import type { ChartPeriod } from './types/periods'
import { cn } from './utils/cn'
import { getCookieValue } from './utils/cookie/cookie'
import { getMetaTags } from './utils/meta/getMetaTags'
import { shouldRevalidateRoot } from './utils/shouldRevalidateRoot'

polyfills?.()

export const links: LinksFunction = () => [
  { rel: 'icon', href: '/favicon.svg?v=2', type: 'image/svg+xml', sizes: 'any' },
  { rel: 'icon', href: '/favicon.ico?v=2' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' },
  { rel: 'stylesheet', href: globalStyles },
  { rel: 'stylesheet', href: appStyles },
]

export const meta: MetaFunction = ({ location }) => {
  return getMetaTags({
    title: APP_META_TITLE,
    description: APP_META_DESCRIPTION,
    url: location.pathname,
  })
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const wagmiCookie = request.headers.get('cookie')
  const isNavbarExpanded = getCookieValue(EXPAND_NAVIGATION_COOKIE_KEY, request) === 'true'
  const isDashboardReturnsCollapsed = getCookieValue(COLLAPSE_DASHBOARD_RETURNS_COOKIE_KEY, request) === 'true' || false
  const apyRange = getCookieValue(APY_RANGE_SETTING_COOKIE_KEY, request)
  const includeRewards = getCookieValue(INCLUDE_REWARDS_SETTING_COOKIE_KEY, request)
  const tvlRange = getCookieValue(TVL_RANGE_SETTING_COOKIE_KEY, request)
  const chartsPeriod = getCookieValue(CHARTS_PERIOD_SETTINGS_COOKIE_KEY, request)
  const initialApyRange =
    apyRange && VALID_APY_RANGES.includes(JSON.parse(apyRange) as VaultApyRange)
      ? (JSON.parse(apyRange) as VaultApyRange)
      : DEFAULT_SETTINGS.apyRange
  const initialIncludeRewards = includeRewards ? JSON.parse(includeRewards) : DEFAULT_SETTINGS.includeRewards
  const initialTvlRange = tvlRange
    ? (() => {
        const parsedValue = JSON.parse(JSON.parse(tvlRange))
        if (
          Array.isArray(parsedValue) &&
          parsedValue.every((item) => typeof item === 'string' || typeof item === 'number')
        ) {
          return parsedValue.map((item) => Number(item)) as [number, number]
        }
        return DEFAULT_SETTINGS.tvlRange
      })()
    : DEFAULT_SETTINGS.tvlRange

  const initialChartsPeriod =
    chartsPeriod && VALID_CHART_PERIODS.includes(JSON.parse(chartsPeriod) as ChartPeriod)
      ? (JSON.parse(chartsPeriod) as ChartPeriod)
      : DEFAULT_SETTINGS.chartsPeriod

  const initialSettings: Settings = {
    apyRange: initialApyRange,
    includeRewards: initialIncludeRewards,
    tvlRange: initialTvlRange,
    chartsPeriod: initialChartsPeriod,
  }

  const { getTheme } = await themeSessionResolver(request)
  const theme = getTheme()

  const clientEnv = {
    MIXPANEL_KEY: process.env.NODE_ENV === 'production' ? process.env.MIXPANEL_PROD_KEY : process.env.MIXPANEL_DEV_KEY,
  }

  return {
    wagmiCookie,
    clientEnv,
    isNavbarExpanded,
    isDashboardReturnsCollapsed,
    initialSettings,
    theme,
    dehydratedState: dehydrate(queryClient),
  }
}

export const shouldRevalidate = shouldRevalidateRoot

export type RootLoaderDataType = NonNullable<ReturnType<typeof useLoaderData<typeof loader>>>

function useSafeLoaderData() {
  try {
    return useLoaderData<typeof loader>()
  } catch (error) {
    console.error('Loader data unavailable, using fallback values', error)
    return null
  }
}

function getCookieBooleanValue(key: string, defaultValue: boolean): boolean {
  try {
    const value = getCookieValue(key)
    return value ? JSON.parse(value) : defaultValue
  } catch {
    return defaultValue
  }
}

interface ProvidersProps {
  children: ReactNode
  loaderData: Awaited<ReturnType<typeof loader>> | null
}

function AppProviders({ children, loaderData }: ProvidersProps) {
  const [wagmiConfig] = useState(() => getWagmiConfig())
  const wagmiInitialState = cookieToInitialState(wagmiConfig, loaderData?.wagmiCookie)

  const isNavbarExpanded = loaderData?.isNavbarExpanded ?? getCookieBooleanValue(EXPAND_NAVIGATION_COOKIE_KEY, false)
  const isDashboardReturnsCollapsed =
    loaderData?.isDashboardReturnsCollapsed ?? getCookieBooleanValue(COLLAPSE_DASHBOARD_RETURNS_COOKIE_KEY, true)

  return (
    <MixpanelProvider mixpanelKey={loaderData?.clientEnv?.MIXPANEL_KEY}>
      <WagmiProvider config={wagmiConfig} initialState={wagmiInitialState}>
        <AccountProvider>
          <QueryClientProvider client={queryClient}>
            <SoundProvider>
              <SettingsProvider initialSettings={loaderData?.initialSettings || DEFAULT_SETTINGS}>
                <LayoutProvider
                  isNavbarPinnedInitially={isNavbarExpanded}
                  isDashboardReturnsCollapsedInitially={isDashboardReturnsCollapsed}
                >
                  <TooltipProvider delayDuration={250}>
                    <ConnectWalletModalProvider>
                      <ThemeProvider specifiedTheme={loaderData?.theme || null} themeAction="/action/set-theme">
                        {children}
                      </ThemeProvider>
                    </ConnectWalletModalProvider>
                  </TooltipProvider>
                </LayoutProvider>
              </SettingsProvider>
            </SoundProvider>
          </QueryClientProvider>
        </AccountProvider>
      </WagmiProvider>
    </MixpanelProvider>
  )
}

interface DocumentProps {
  children: ReactNode
  loaderData: Awaited<ReturnType<typeof loader>> | null
}

function AppDocument({ children, loaderData }: DocumentProps) {
  const [theme] = useTheme()

  return (
    <html lang="en" className={cn('overscroll-none', theme)}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <PreventFlashOnWrongTheme ssrTheme={Boolean(loaderData?.theme)} />
        <Links />
      </head>
      <body>
        <DesktopLayout>
          <HydrationBoundary state={loaderData?.dehydratedState}>{children}</HydrationBoundary>
        </DesktopLayout>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export function ErrorBoundary() {
  const loaderData = useSafeLoaderData()

  return (
    <AppProviders loaderData={loaderData}>
      <AppDocument loaderData={loaderData}>
        <ErrorPage />
      </AppDocument>
    </AppProviders>
  )
}

export default function App() {
  const loaderData = useLoaderData<typeof loader>()
  useViewTransition()

  return (
    <AppProviders loaderData={loaderData}>
      <AppDocument loaderData={loaderData}>
        <Outlet />
      </AppDocument>
    </AppProviders>
  )
}
